import { useRef } from "react";
import SecondaryButton from "@/components/buttons/secondary-button/SecondaryButton";
import { Icon } from "@/components/icons/Icon";
import { useDevice } from "@/store/useDevice";

export type ImageUploaderProps = {
  files: File[];
  onChange: (files: File[]) => void;
  accept?: string;
  maxFiles?: number;
  disabled?: boolean;
  buttonText: string;
  cameraButtonText: string;
  className?: string;
  images: string[];
};

const ACCEPT = "image/png,image/jpeg,image/webp";

export default function ImageUploader({
  images,
  files,
  onChange,
  maxFiles,
  disabled,
  buttonText,
  cameraButtonText,
  className = "",
}: ImageUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const isMobile = useDevice((state) => state.isMobile);

  const openPicker = () => {
    if (!disabled) inputRef.current?.click();
  };

  const openCamera = () => {
    if (!disabled) cameraInputRef.current?.click();
  };

  const isUploadDisabled =
    disabled ||
    (typeof maxFiles === "number" && files.length + images.length >= maxFiles);

  const handleSelect: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const list = Array.from(e.target.files ?? []);
    const allowedTypes = ["image/png", "image/jpeg", "image/webp"];
    const images = list.filter((f) => allowedTypes.includes(f.type));
    const merged = [...files, ...images].filter(
      (f, i, arr) =>
        arr.findIndex((x) => x.name === f.name && x.size === f.size) === i,
    );
    const limited =
      typeof maxFiles === "number" ? merged.slice(0, maxFiles) : merged;
    onChange(limited);
    e.currentTarget.value = "";
  };

  const removeAt = (idx: number) => {
    const next = files.slice();
    next.splice(idx, 1);
    onChange(next);
  };

  return (
    <div className={className}>
      <ul className="space-y-2">
        {images.map((file, i) => (
          <li
            key={`${file}-${file}-${i}`}
            className="flex items-center justify-between rounded-sm border border-dashed border-gray-300 px-3 py-2"
          >
            <span className="flex items-center gap-2 text-sm ">
              <Icon name="image" className="size-4 text-black" />
              <span className="truncate text-ib-gray-darkest max-w-[220px]">
                {file}
              </span>
            </span>
          </li>
        ))}
        {files.map((file, i) => (
          <li
            key={`${file.name}-${file.size}-${i}`}
            className="flex items-center justify-between rounded-sm border border-dashed border-gray-300 px-3 py-2"
          >
            <span className="flex items-center gap-2 text-sm ">
              <Icon name="image" className="size-4 text-black" />
              <span className="truncate text-ib-gray-darkest max-w-[220px]">
                {file.name}
              </span>
            </span>
            <button
              type="button"
              onClick={() => removeAt(i)}
              className="p-1 text-red-600 hover:text-red-700"
              aria-label={`Eliminar ${file.name}`}
            >
              <Icon name="trash" className="size-6" />
            </button>
          </li>
        ))}
      </ul>
      <div className="flex flex-col gap-3 mt-3 md:flex-row">
        <div className="w-full md:w-auto">
          <SecondaryButton
            disabled={isUploadDisabled}
            icon="addImage"
            label={buttonText}
            colour={"red"}
            onClick={openPicker}
            iconClassName="size-6"
          />
        </div>

        {isMobile && (
          <div className="w-full md:w-auto">
            <SecondaryButton
              disabled={isUploadDisabled}
              icon="addImage"
              label={cameraButtonText}
              colour="red"
              onClick={openCamera}
              iconClassName="size-6"
            />
          </div>
        )}

        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          multiple
          onChange={handleSelect}
          className="hidden"
          aria-hidden
          tabIndex={-1}
          data-testid="image-picker-input"
        />

        {isMobile && (
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleSelect}
            className="hidden"
            aria-hidden
            tabIndex={-1}
            data-testid="camera-input"
          />
        )}
      </div>
    </div>
  );
}
